import React, { useEffect } from "react";
import { Box, FormControl, MenuItem, SelectChangeEvent, Typography } from "@mui/material";
import { useAppDispatch } from "src/store/store";
import { useHistory } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "@mui/material";

import {
  setWithdrawalAmount,
  setSelectedBankAccount,
  useWithdrawalSlice,
  resetWithdrawalThunk,
  resetInput,
} from "src/store/features/withdrawal.slice";
import { BankAccount } from "src/store/features/withdrawal.slice";
import { HoldingRes } from "src/store/features/api/generated";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import {
  MaxButton,
  StyledAmountInput,
  StyledAmountInputContainer,
  StyledBalanceText,
  StyledErrorBox,
  StyledIconWrapper,
  StyledInfoBox,
  StyledInfoHeader,
  StyledInfoText,
  StyledInfoTitle,
  StyledLabel,
  StyledSelect,
} from "../common/styles/FiatWithdrawalStyled";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import InfoIcon from "src/icons/portfolio/InfoIcon";

interface FiatWithdrawalStageInputDetailsProps {
  selectedBankAccount: BankAccount | null;
  withdrawalAmount: number;
  bankAccounts: BankAccount[];
  defaultCurrencyHolding?: HoldingRes;
  onMaxAmount: () => void;
  onInputErrorChange?: (errors: { selectError: boolean; amountError: boolean }) => void;
  showMinAmountError: boolean;
  showInsufficientBalance: boolean;
  showNoCurrencyError?: boolean;
}

export function FiatWithdrawalStageInputDetails({
  selectedBankAccount,
  withdrawalAmount,
  bankAccounts,
  defaultCurrencyHolding,
  onMaxAmount,
  onInputErrorChange,
  showMinAmountError,
  showInsufficientBalance = false,
  showNoCurrencyError,
}: FiatWithdrawalStageInputDetailsProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { error } = useWithdrawalSlice();

  const [localAmountError, setLocalAmountError] = React.useState(false);

  // exclude default message from error
  const selectError = Boolean(error === "Please select a bank account");
  // exclude major error messages from amount error as this is handled separately
  const amountError = Boolean(
    error && error.toLowerCase().includes("amount") && !error.toLowerCase().includes("minimum 100"),
  );

  // Call onInputErrorChange when errors change
  React.useEffect(() => {
    if (onInputErrorChange) {
      onInputErrorChange({ selectError, amountError });
    }
  }, [selectError, amountError, onInputErrorChange]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Validate: non-negative, up to 2 decimals
    const dollarRegex = /^\d+(\.\d{0,2})?$/;
    setLocalAmountError(false);
    dispatch(setWithdrawalAmount(parseFloat(value)));
  };

  const handleBankAccountChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value === "add-new") {
      history.push("/bank-accounts");
      return;
    }

    const selectedAccount = bankAccounts.find((account) => account.id === event.target.value);

    if (selectedAccount) {
      dispatch(setSelectedBankAccount(selectedAccount));
    }
  };

  return (
    <Box sx={{ width: "100%", paddingBottom: "28px" }}>
      {bankAccounts.length === 0 ? (
        <StyledInfoBox>
          <StyledInfoHeader>
            <InfoIcon />
            <StyledInfoTitle>No bank account added yet</StyledInfoTitle>
          </StyledInfoHeader>
          <StyledInfoText>
            Please add a bank account to your account to proceed with fiat withdrawals. You can find
            more information on this{" "}
            <Link
              component="button"
              onClick={() => history.push("/bank-accounts")}
              sx={{
                textDecoration: "underline",
                color: "#002035",
                fontWeight: 500,
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                cursor: "pointer",
              }}>
              here
            </Link>
            .
          </StyledInfoText>
        </StyledInfoBox>
      ) : (
        <Box>
          <Box sx={{ flex: 1, paddingBottom: "28px" }}>
            <StyledLabel>Select Bank account</StyledLabel>
            <FormControl fullWidth>
              <StyledSelect
                value={selectedBankAccount?.id || ""}
                displayEmpty
                onChange={handleBankAccountChange}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Box sx={{ color: "#6B7280" }}>Select Bank account</Box>;
                  }
                  const account = bankAccounts.find((acc) => acc.id === selected);
                  return account ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CountryOrCryptoFlag name={account.currency} size={20} />
                      <span>
                        {account.currency} - {account.accountName}
                      </span>
                    </Box>
                  ) : null;
                }}>
                {bankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id} sx={{ lineHeight: "30px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CountryOrCryptoFlag name={account.currency} size={20} />
                      <span>
                        {account.currency} - {account.accountName}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem
                  value="add-new"
                  sx={{
                    justifyContent: "center",
                    lineHeight: "30px",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    color: "primary.main",
                  }}>
                  Add bank account
                </MenuItem>
              </StyledSelect>
              {selectError && !showNoCurrencyError && (
                <StyledErrorBox>
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  {error}
                </StyledErrorBox>
              )}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <StyledLabel>Amount to Withdraw</StyledLabel>
            <StyledAmountInputContainer
              className={
                amountError || localAmountError || showMinAmountError || showInsufficientBalance
                  ? "error"
                  : showNoCurrencyError
                  ? "disabled"
                  : ""
              }>
              <StyledIconWrapper>
                <CountryOrCryptoFlag
                  name={
                    selectedBankAccount?.currency ||
                    defaultCurrencyHolding?.currencyConfig?.code ||
                    "USD"
                  }
                  size={20}
                />
              </StyledIconWrapper>
              <StyledAmountInput
                fullWidth
                placeholder="0.00"
                type="number"
                value={withdrawalAmount || ""}
                onChange={handleAmountChange}
                disabled={showNoCurrencyError}
                onBlur={(event) => {
                  const value = event.target.value;
                  // don't allow empty input
                  if (value === "" || value === "0" || value === "0.0" || value === "0.00") {
                    setLocalAmountError(true);
                    return;
                  }
                }}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  inputProps: {
                    max: 100000000,
                  },
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center" }}>
                {defaultCurrencyHolding?.available && (
                  <StyledBalanceText>
                    {formatCurrency(
                      defaultCurrencyHolding.available,
                      selectedBankAccount?.currency ||
                        defaultCurrencyHolding?.currencyConfig?.code ||
                        "USD",
                    )}{" "}
                  </StyledBalanceText>
                )}
                <MaxButton
                  onClick={onMaxAmount}
                  variant="contained"
                  size="small"
                  disabled={showInsufficientBalance}>
                  Max
                </MaxButton>
              </Box>
            </StyledAmountInputContainer>
            {amountError && !showNoCurrencyError && (
              <StyledErrorBox>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                {error}
              </StyledErrorBox>
            )}
            {localAmountError && !showNoCurrencyError && (
              <StyledErrorBox>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                Please enter a valid amount
              </StyledErrorBox>
            )}
            {showNoCurrencyError && (
              <StyledErrorBox>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                You don't have enough balance in this currency to withdraw.
              </StyledErrorBox>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
